import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
  viewChild,
} from '@angular/core';
import { CardComponent } from '@core/components/card/card.component';
import { UserCommonInfoComponent } from '@features/user/user-common-info/user-common-info.component';
import { SvgIconComponent } from 'angular-svg-icon';
import { CountUpModule } from 'ngx-countup';
import { AdminUser } from '@features/admin/admin-users/admin-user.interface';
import { GamesService } from '@core/services/games.service';
import {
  FormControl,
  FormGroup,
  NgForm,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { SocketService } from '@core/services/socket.service';
import { ToastrService } from 'ngx-toastr';
import { DatePipe } from '@angular/common';
import { NgpTooltip, NgpTooltipTrigger } from 'ng-primitives/tooltip';
import { ClipboardService } from '@core/services/clipboard.service';
import { FormFieldComponent } from '@core/components/form-field/form-field.component';
import { muteTimeValidator } from './mute-time.validator';
import { banTimeValidator } from './ban-time.validator';

@Component({
  selector: 'app-admin-user-profile',
  imports: [
    SvgIconComponent,
    UserCommonInfoComponent,
    CardComponent,
    CountUpModule,
    ReactiveFormsModule,
    FormFieldComponent,
    DatePipe,
    NgpTooltip,
    NgpTooltipTrigger,
  ],
  templateUrl: './admin-user-profile.component.html',
  styleUrl: './admin-user-profile.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  host: {
    '[class.hide-message]': 'hidePrimaryData()',
  },
})
export class AdminUserProfileComponent {
  user = model.required<AdminUser>();
  hidePrimaryData = input<boolean>(false);
  private gamesService = inject(GamesService);
  private socketService = inject(SocketService);
  private toastrService = inject(ToastrService);
  private clipboardService = inject(ClipboardService);

  banFormRef = viewChild.required<NgForm>('banFormRef');
  muteFormRef = viewChild.required<NgForm>('muteFormRef');

  countUpOptionsFormat = {
    decimalPlaces: 2,
    duration: 1,
    prefix: '$',
  };

  countUpOptionsNoFormat = {
    duration: 1,
  };

  stats = computed(() => {
    return [
      {
        icon: this.gamesService.gamesMap().Coinflip.icon,
        label: 'Coinflip Deposited',
        value: this.user().cf_deposited,
        color: 'red',
      },
      {
        icon: this.gamesService.gamesMap().Coinflip.icon,
        label: 'Coinflip Played',
        value: this.user().cf_played,
        color: 'red',

        noFormat: true,
      },
      {
        icon: this.gamesService.gamesMap().Jackpot.icon,
        label: 'Jackpot Deposited',
        value: this.user().jp_deposited,
        color: 'green',
      },
      {
        icon: this.gamesService.gamesMap().Jackpot.icon,
        label: 'Jackpot Played',
        value: this.user().jp_played,
        color: 'green',

        noFormat: true,
      },
      {
        icon: this.gamesService.gamesMap()['Case Battles'].icon,
        label: 'Case Battles Balance',
        value: this.user().cb_balance,
        color: 'purple',
      },
      {
        icon: this.gamesService.gamesMap()['Case Battles'].icon,
        label: 'Case Battles Earned',
        value: this.user().cb_earned,
        color: 'purple',

        noFormat: true,
      },
      {
        icon: 'assets/icons/core/skins.svg',
        label: 'Balance',
        value: this.user().balance,
        color: 'blue',
      },
      {
        icon: 'assets/icons/core/pure.svg',
        label: 'Pure Balance',
        value: this.user().pure_balance,
        color: 'blue',
      },
      {
        icon: 'assets/icons/core/price.svg',
        label: 'Ref Balance',
        value: this.user().ref_balance,
        color: 'gray',
      },
      {
        icon: 'assets/icons/core/price.svg',
        label: 'Ref Earned',
        value: this.user().ref_earned,
        color: 'gray',
      },
      {
        icon: 'assets/icons/core/skins.svg',
        label: 'Skins Deposited',
        value: this.user().skins_deposited_value,
        color: 'yellow',
      },
      {
        icon: 'assets/icons/core/price.svg',
        label: 'Store Withdrawn',
        value: this.user().store_withdrawn,
        color: 'yellow',
      },
    ];
  });

  muteTime = new FormControl<number>(0, [
    Validators.required,
    muteTimeValidator(),
  ]);
  muteReason = new FormControl<string | null>('');

  muteForm = new FormGroup({
    time: this.muteTime,
    reason: this.muteReason,
  });

  banTime = new FormControl<number>(0, [
    Validators.required,
    banTimeValidator(),
  ]);
  banReason = new FormControl<string | null>('');

  banForm = new FormGroup({
    time: this.banTime,
    reason: this.banReason,
  });

  ticketBanForm = new FormGroup({});

  constructor() {
    afterRenderEffect(() => {
      if (this.user().ban_time !== 0) {
        this.banReason.setValue(this.user().ban_reason);
        if (this.user().ban_time === -1) {
          this.banTime.setValue(-1);
        } else {
          const currentTime = Math.floor(Date.now() / 1000);
          const banEndTime = this.user().ban_time;
          const remainingSeconds = banEndTime - currentTime;
          const remainingMinutes = Math.max(
            0,
            Math.ceil(remainingSeconds / 60)
          );

          this.banTime.setValue(remainingMinutes);
        }
      }

      if (this.user().mute_time !== 0) {
        this.muteReason.setValue(this.user().mute_reason);
        if (this.user().mute_time === -1) {
          this.muteTime.setValue(-1);
        } else {
          const currentTime = Math.floor(Date.now() / 1000);
          const muteEndTime = this.user().mute_time;
          const remainingSeconds = muteEndTime - currentTime;
          const remainingMinutes = Math.max(
            0,
            Math.ceil(remainingSeconds / 60)
          );

          this.muteTime.setValue(remainingMinutes);
        }
      }
    });
  }

  private async reloadUser() {
    const result = await this.socketService.emit('admin_load_users', {
      order: 'default',
      filter: 'steamid',
      text: this.user().steamid,
      page: 1,
      order_by: 'asc',
    });

    if (result[0]) {
      this.user.update((prev) => ({
        ...prev,
        ...result[0],
      }));
    }
  }

  public async submitBan() {
    const user = this.user();

    const isNotBanned = user.ban_time === 0;
    const isBanned = user.ban_time !== 0;

    const time = Number(this.banForm.get('time')?.value);
    const reason = this.banForm.get('reason')?.value;

    if (isNotBanned) {
      if (!this.banForm.valid) {
        const banTimeError = this.banTime.errors?.['banTime'];
        if (banTimeError) {
          this.toastrService.error(banTimeError.message);
        } else {
          this.toastrService.error(
            'Form is not valid. Please check the inputs.'
          );
        }
        return;
      }

      try {
        await this.socketService.emit('admin_ban_user', {
          steamid: this.user().steamid,
          time,
          reason,
        });

        await this.reloadUser();
        this.banFormRef().resetForm();

        this.toastrService.success(
          `User ${this.user().name} has been banned successfully`
        );
      } catch (error: any) {
        console.log(
          '🚀 ~ AdminUserProfileComponent ~ submitBan ~ error:',
          error
        );
        this.toastrService.error(error);
      }
    }

    if (isBanned) {
      try {
        await this.socketService.emit('admin_unban_user', {
          steamid: this.user().steamid,
        });

        this.banFormRef().resetForm();
        this.banForm.reset({ time: 0, reason: '' });

        await this.reloadUser();

        this.toastrService.success(
          `User ${this.user().name} has been unbanned successfully`
        );
      } catch (error: any) {
        console.log(
          '🚀 ~ AdminUserProfileComponent ~ submitBan ~ error:',
          error
        );
        this.toastrService.error(error);
      }
    }
  }

  public async submitMute() {
    const user = this.user();

    const isNotMuted = user.mute_time === 0;
    const isMuted = user.mute_time !== 0;

    const time = Number(this.muteForm.get('time')?.value);
    const reason = this.muteForm.get('reason')?.value;

    if (isNotMuted) {
      if (!this.muteForm.valid) {
        const muteTimeError = this.muteTime.errors?.['muteTime'];
        if (muteTimeError) {
          this.toastrService.error(muteTimeError.message);
        } else {
          this.toastrService.error(
            'Form is not valid. Please check the inputs.'
          );
        }
        return;
      }

      try {
        await this.socketService.emit('admin_mute_user', {
          steamid: this.user().steamid,
          time,
          reason,
        });

        await this.reloadUser();
        this.muteFormRef().resetForm();

        this.toastrService.success(
          `User ${this.user().name} has been muted successfully`
        );
      } catch (error: any) {
        console.log(
          '🚀 ~ AdminUserProfileComponent ~ submitMute ~ error:',
          error
        );
        this.toastrService.error(error);
      }
    }

    if (isMuted) {
      try {
        await this.socketService.emit('admin_unmute_user', {
          steamid: this.user().steamid,
        });

        this.muteFormRef().resetForm();
        this.muteForm.reset({ time: 0, reason: '' });

        await this.reloadUser();

        this.toastrService.success(
          `User ${this.user().name} has been unmuted successfully`
        );
      } catch (error: any) {
        console.log(
          '🚀 ~ AdminUserProfileComponent ~ submitMute ~ error:',
          error
        );
        this.toastrService.error(error);
      }
    }
  }

  public async submitTicketBan() {
    const user = this.user();

    try {
      await this.socketService.emit('admin_ticket_ban', {
        steamid: user.steamid,
        action: user.ticket_ban === 0 ? 1 : 0,
      });
      this.toastrService.success(
        `User ${user.name} has been ${
          user.ticket_ban === 0 ? 'ticket banned' : 'ticket unbanned'
        } successfully`
      );
      await this.reloadUser();
    } catch (error: any) {
      console.log(
        '🚀 ~ AdminUserProfileComponent ~ submitTicketBan ~ error:',
        error
      );
      this.toastrService.error(error);
    }
  }

  public copyTradeURL() {
    this.clipboardService.copy(this.user().tradelink, 'Trade URL copied');
  }
}
